import { FC, Suspense } from 'react';
import { FaFile, FaFolder } from 'react-icons/fa';
import { useParams } from 'react-router';
import { SectionLink } from '../../components/links/SectionLink';
import { DocumentListSection } from '../../components/oneDrive/DocumentList';
import { NavPage } from '../../components/page/Breadcrumbs';
import { ContentSection } from '../../components/page/ContentSection';
import { PageContainer } from '../../components/page/PageContainer';
import { PageHeader, PageHeaderSize } from '../../components/page/PageHeader';
import { PageSizer } from '../../components/page/PageSizer';
import { SectionHeader } from '../../components/page/SectionHeader';
import { useSections } from '../../services/graph/hooks/category';
import { useFilesInFolder } from '../../services/graph/hooks/documentHelper';
import { Loading } from '../site/Loading';
import strings from './documents.translations';
import { useCategoryDetails } from '../../services/api/hooks/categories';

export const CategoryPage: FC = () => {
  const { categoryId } = useParams<'categoryId'>();
  if (!categoryId) throw new Error('No category id provided');

  const { categoryData, imageUrl } = useCategoryDetails(categoryId);

  const sections = useSections(categoryId);

  const breadcrumbs: NavPage[] = [
    {
      name: strings.common.pages.documents,
      to: '/documents'
    },
    {
      name: categoryData.name,
      to: `/documents/categories/${categoryId}`
    }
  ];

  return (
    <PageContainer>
      <PageHeader
        title={categoryData.name}
        description={categoryData.description}
        imageUrl={imageUrl}
        breadcrumbs={breadcrumbs}
        size={PageHeaderSize.LARGE}
      />
      <Suspense fallback={<Loading fullPage={false} />}>
        {categoryData.longDescription && (
          <ContentSection>
            <p className="whitespace-pre-wrap">{categoryData.longDescription}</p>
          </ContentSection>
        )}
        <PageSizer className="py-5 flex flex-col gap-10">
          {sections.length > 0 && (
            <div>
              <SectionHeader title={strings.sections} Icon={FaFolder} className="mb-5"></SectionHeader>
              <div className="grid gap-5 grid-cols-1 sm:grid-cols-2">
                {sections.map(section => {
                  if (!section.id) throw new Error('No section id');
                  return <SectionLink driveItemId={section.id} key={section.id}></SectionLink>;
                })}
              </div>
            </div>
          )}
          <DocumentListSection
            title={categoryData.name}
            Icon={FaFile}
            filterable
            getDocuments={() => useFilesInFolder(categoryId)}
          />
        </PageSizer>
      </Suspense>
    </PageContainer>
  );
};
